import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Radio,
  Text,
} from "@mantine/core";
import { IconHelpCircle, IconPlayerStopFilled } from "@tabler/icons-react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { BoxDrawImage } from "@labeling-tools/ui-components/src/box-draw-image";
import {
  useFetchJob,
  useNextPendingTask,
  useCompleteJobTask,
  useSkipJobTask,
  useFetchParcel,
  useImageGalleryQuery,
  useBenchmarkQuizMetrics,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { JobStatus } from "@augmodo/lebron-browser-client";
import { useQueryClient } from "@tanstack/react-query";
import { useGate } from "statsig-react";
import { QuizMetricCard } from "@/components/QuizMetricCard";
import {
  getNextPendingTaskData,
  resolveProductDetections,
} from "@labeling-tools/out-of-stock/src/utils/helpers";
import { TaskCounter } from "@labeling-tools/out-of-stock/src/components/TaskCounter";
import { useCoverageState, type BoxCategory } from "@/hooks/useCoverageState";
import { useCoveragePrefetch } from "@/hooks/useCoveragePrefetch";
import { useProfileHome } from "@/hooks/useProfileHome";
import {
  CoverageSidebar,
  COVERAGE_SIDEBAR_WIDTH,
} from "@/components/coverage/CoverageSidebar";
import { CoverageRightPanel } from "@/components/coverage/CoverageRightPanel";
import {
  PlanogramSearchModal,
  type PlanogramSlotEdit,
  type PlanogramSearchError,
} from "@/components/coverage/PlanogramSearchModal";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";
import {
  IdleTimeProvider,
  IdleWarningModal,
  ExitModal,
} from "@labeling-tools/ui-components/src";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import {
  HEADER_HEIGHT,
  PLAY_MODE_JOB_TYPE,
} from "@labeling-tools/out-of-stock/src/config/data";
import {
  useAppDispatch,
  useAppSelector,
} from "@labeling-tools/out-of-stock/src/store/hooks";
import { setIsPlayMode } from "@labeling-tools/out-of-stock/src/store";
import { CoverageTrainingOverlay } from "@/components/coverage/CoverageTrainingOverlay";
import { ImageFilmstrip } from "@/components/coverage/ImageFilmstrip";
import { getAisleIndexForBox } from "@/utils/geometry";
import {
  boxImageId,
  getGroupImages,
  hiddenIndicesForImage,
  withImageId,
} from "@/utils/groupedImages";
import { useDevFixtures } from "@/dev";
import { useFetchNextPlayModeJob } from "@/hooks/useFetchNextPlayModeJob";
import type {
  JobTaskDetailSchema,
  OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import { isDev } from "@/utils/utils";

interface CoverageTaskProps {
  mode?: "admin";
  adminTask?: JobTaskDetailSchema;
  adminParcel?: OoSParcelSchema;
  adminResult?: Record<string, unknown>;
}

export const CoverageTask = ({
  mode,
  adminTask,
  adminParcel,
  adminResult,
}: CoverageTaskProps = {}) => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const queryClient = useQueryClient();
  const isAdmin = mode === "admin";
  const dispatch = useAppDispatch();
  const isPlayMode = useAppSelector((state) => state.oos.isPlayMode);
  const [noNextJobAvailable, setNoNextJobAvailable] = useState(false);

  // Dev mock support — context defaults to { active: false } outside mock mode
  const devFixtures = useDevFixtures();

  // Data fetching
  const { data: job } = useFetchJob(jobId ?? "");
  const {
    data: nextPendingTask,
    isLoading: taskLoading,
    isError: taskError,
  } = useNextPendingTask(jobId, !isAdmin);

  // Whether this is a quiz has to come from the *job*, not the task — once
  // the last question is answered there is no next pending task, so a
  // task-level check alone would be false exactly when the quiz just
  // finished. Mirrors OosConfirmationContent.tsx's isBenchmarkJob. String
  // literal until @augmodo/lebron-browser-client picks up the enum.
  const isBenchmarkJob = job?.type === "COVERAGE_REVIEW_BENCHMARK_TASKS";
  const { value: benchmarkEnabled, isLoading: benchmarkGateLoading } = useGate(
    "fe_enable_oos_benchmark_quiz",
  );
  // Benchmark quiz tasks are served only to the treatment group; everyone
  // else gets the same empty state as an exhausted job — see the isLoading
  // and taskError branches below.
  const isBenchmarkTask =
    (nextPendingTask as any)?.type === "COVERAGE_REVIEW_BENCHMARK";

  // Scored results for a quiz the reviewer just finished. Gated with the rest
  // of the benchmark feature so a treatment-group-only quiz can't leak a
  // card. Keyed off `taskError` (this component's "no next task" signal,
  // unlike OosConfirmationContent's gallery-based isEmptyState) so it only
  // fires once the quiz is actually out of questions.
  const { data: quizMetrics, isLoading: quizMetricsLoading } =
    useBenchmarkQuizMetrics(
      !isAdmin && isBenchmarkJob && benchmarkEnabled && taskError
        ? (jobId ?? null)
        : null,
    );

  // When play mode isn't active (or is already exhausted), just navigate
  // away -- the actual "fetch the next job" attempt is wired up below, once
  // activePlanogramId is available (see useFetchNextPlayModeJob).
  useEffect(() => {
    if (isAdmin || !taskError || isBenchmarkJob) return;
    if (!isPlayMode || noNextJobAvailable) {
      navigate(`${jobsPath}?tab=coverage`);
    }
  }, [
    taskError,
    isAdmin,
    isBenchmarkJob,
    isPlayMode,
    noNextJobAvailable,
    navigate,
    jobsPath,
  ]);

  const activeTask = isAdmin ? adminTask : nextPendingTask;
  const event = activeTask?.data;
  const { data: fetchedParcel, isLoading: parcelLoading } = useFetchParcel(
    isAdmin ? undefined : (event?.parcel_id as string | undefined),
  );
  const parcel = isAdmin ? adminParcel : fetchedParcel;
  const nextPendingTaskData = getNextPendingTaskData(parcel, event, activeTask);

  const imageIdToFetch = nextPendingTaskData?.image_id;

  // Fetch shelf image — query variant so the prefetch cache is shared
  const { data: mainImageGallery, isLoading: mainImageLoading } =
    useImageGalleryQuery(imageIdToFetch ? [imageIdToFetch] : undefined);

  const groupImages = useMemo(
    () => getGroupImages(parcel, imageIdToFetch),
    [parcel, imageIdToFetch],
  );
  const siblingImageIds = useMemo(
    () => groupImages.filter((image) => !image.isPrimary).map((i) => i.imageId),
    [groupImages],
  );
  // Separate query from the primary's so useCoveragePrefetch's cache key holds
  const { data: siblingGallery, isLoading: siblingImagesLoading } =
    useImageGalleryQuery(
      siblingImageIds.length > 0 ? siblingImageIds : undefined,
    );

  const [activeImageId, setActiveImageId] = useState<string | null>(null);
  const effectiveImageId = activeImageId ?? imageIdToFetch ?? "";
  const isPrimaryImageActive = effectiveImageId === imageIdToFetch;

  const groupImageUrls = useMemo(() => {
    const map = new Map<string, string>();
    for (const image of mainImageGallery?.images ?? []) {
      if (image.id && image.image) map.set(image.id, image.image);
    }
    for (const image of siblingGallery?.images ?? []) {
      if (image.id && image.image) map.set(image.id, image.image);
    }
    return map;
  }, [mainImageGallery, siblingGallery]);

  const shelfImageUrl = isPrimaryImageActive
    ? mainImageGallery?.images[0]?.image
    : groupImageUrls.get(effectiveImageId);

  // Prefetch the next task while the user works on the current one
  const { nextTaskId: prefetchNextTaskId, isPrefetchReady } =
    useCoveragePrefetch({
      jobId,
      currentTaskId: nextPendingTask?.id ?? undefined,
      enabled: !isAdmin && !!nextPendingTaskData,
    });

  // Empty areas for BoxDrawImage
  const emptyAreasPoints = useMemo(() => {
    if (isAdmin && adminResult?.empty_areas) {
      return (adminResult.empty_areas as any[]).map((area: any) => ({
        points: area.points || [],
      }));
    }
    if (!nextPendingTaskData?.data?.empty_areas) return [];
    return nextPendingTaskData.data.empty_areas.map((area: any) => ({
      points: area.points || [],
    }));
  }, [
    isAdmin,
    adminResult?.empty_areas,
    nextPendingTaskData?.data?.empty_areas,
  ]);

  // Aligned with emptyAreasPoints.
  const existingBoxImageIds = useMemo<(string | null)[]>(() => {
    const source =
      isAdmin && adminResult?.empty_areas
        ? adminResult.empty_areas
        : nextPendingTaskData?.data?.empty_areas;
    if (!Array.isArray(source)) return [];
    return source.map((area: any) => area?.image_id ?? null);
  }, [
    isAdmin,
    adminResult?.empty_areas,
    nextPendingTaskData?.data?.empty_areas,
  ]);

  // Aisle detections for spatial assignment
  const aisleDetections = useMemo(() => {
    const detections = (parcel?.query as any)?.aisle_detections;
    if (!Array.isArray(detections)) return [];
    return detections.map((ad: any) => ({ points: ad.points ?? [] }));
  }, [parcel?.query]);

  // --- Multi-planogram slot state ---
  const basePlanogramIds: string[] = useMemo(() => {
    const aisleMapping = parcel?.query?.aisle_to_planogram as
      | string[]
      | undefined;
    return aisleMapping ?? [];
  }, [parcel?.query?.aisle_to_planogram]);

  const [overridePlanogramIds, setOverridePlanogramIds] = useState<
    Map<number, string>
  >(new Map());
  const [extraPlanogramIds, setExtraPlanogramIds] = useState<string[]>([]);
  const [activePlanogramTab, setActivePlanogramTab] = useState(0);
  const [planogramSearchOpen, setPlanogramSearchOpen] = useState(false);
  const [modalPreselectedSlot, setModalPreselectedSlot] = useState<
    number | null
  >(null);
  const [planogramSearchError, setPlanogramSearchError] =
    useState<PlanogramSearchError | null>(null);

  // Effective planogram ID per slot (base + overrides + user-added extras)
  const planogramSlots = useMemo(() => {
    const slots = basePlanogramIds.map((baseId, i) => ({
      aisleIndex: i,
      planogramId: overridePlanogramIds.get(i) ?? baseId,
    }));
    // Append user-added extra planograms
    extraPlanogramIds.forEach((id, i) => {
      slots.push({ aisleIndex: basePlanogramIds.length + i, planogramId: id });
    });
    return slots;
  }, [basePlanogramIds, overridePlanogramIds, extraPlanogramIds]);

  const activePlanogramId =
    planogramSlots[activePlanogramTab]?.planogramId ?? null;

  const onNoNextJobFound = useCallback(() => {
    setNoNextJobAvailable(true);
    dispatch(setIsPlayMode(false));
    navigate(`${jobsPath}?tab=coverage`);
  }, [dispatch, navigate, jobsPath]);

  useFetchNextPlayModeJob({
    enabled:
      !isAdmin &&
      !!taskError &&
      !isBenchmarkJob &&
      isPlayMode &&
      !noNextJobAvailable,
    jobId,
    jobType: PLAY_MODE_JOB_TYPE["coverage"].jobType,
    route: PLAY_MODE_JOB_TYPE["coverage"].route,
    livePlanogramId: activePlanogramId,
    onNoJobFound: onNoNextJobFound,
  });

  // Fetch planogram data for ALL slots
  const plano0 = useAtlasPlanogram(planogramSlots[0]?.planogramId);
  const plano1 = useAtlasPlanogram(planogramSlots[1]?.planogramId);
  const plano2 = useAtlasPlanogram(planogramSlots[2]?.planogramId);
  const plano3 = useAtlasPlanogram(planogramSlots[3]?.planogramId);

  const allPlanogramQueries = useMemo(
    () => [plano0, plano1, plano2, plano3].slice(0, planogramSlots.length),
    [plano0, plano1, plano2, plano3, planogramSlots.length],
  );

  const activePlanogramData = allPlanogramQueries[activePlanogramTab]?.data;
  const activePlanogramLoading =
    allPlanogramQueries[activePlanogramTab]?.isLoading ?? false;

  // Merge product image URLs from ALL planograms
  const productImageUrls = useMemo(() => {
    const map = new Map<string, string>();
    for (const query of allPlanogramQueries) {
      if (query?.data?.products) {
        for (const product of query.data.products) {
          if (product.image_url) {
            map.set(product.product_id, product.image_url);
          }
        }
      }
    }
    return map;
  }, [allPlanogramQueries]);

  // Merge product name maps from ALL planograms
  const productNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const query of allPlanogramQueries) {
      if (query?.data?.products) {
        for (const product of query.data.products) {
          map.set(product.product_id, product.description || "Unknown product");
        }
      }
    }
    return map;
  }, [allPlanogramQueries]);

  // Merge planogram name maps from ALL planograms
  const planogramNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const query of allPlanogramQueries) {
      if (query?.data?.id && query?.data?.description) {
        map.set(query.data.id, query.data.description);
      }
    }
    return map;
  }, [allPlanogramQueries]);

  // Build planogram slot labels for tabs
  const planogramSlotLabels = useMemo(() => {
    return planogramSlots.map((slot, i) => {
      const name = planogramNameMap.get(slot.planogramId);
      return {
        aisleIndex: i,
        planogramId: slot.planogramId,
        label: name ?? `Planogram ${i + 1}`,
      };
    });
  }, [planogramSlots, planogramNameMap]);

  // Coverage state (extends box draw)
  const coverageState = useCoverageState();
  const {
    selectedExistingBoxIndex,
    setSelectedExistingBoxIndex,
    selectedUserDrawnBoxIndex,
    setSelectedUserDrawnBoxIndex,
    hiddenExistingBoxIndices,
    hiddenUserDrawnBoxIndices,
    deletedBoxIndices,
    userDrawnBoxes,
    handleBoxView,
    handleBoxDelete,
    handleBoxRestore,
    handleUserDrawnBoxesChange,
    productSelections,
    pendingSelectionBoxKey,
    getBoxKey,
    toggleProductInSelection,
    getSelectedProductIds,
    getSelectedProductCount,
    confirmProductSelection,
    removeProductSelection,
    requestProductSelection,
    dismissProductSelection,
    canSubmit,
    resetCoverageState,
    getBoxCategory,
    moveBoxToCategory,
    getUnassignedCount,
    assignBoxToSlot,
    getBoxSlot,
    clearSelectionsForSlot,
    setBoxCategories,
    setProductSelections,
    setBoxPlanogramSlot,
  } = coverageState;

  // Boxes stay one flat array with stable indices no matter which view is
  // shown, since query_empty_area_index and the box keys ride on that index.
  // Switching views only folds the other views' boxes into the hidden sets.
  const hiddenExistingForImage = useMemo(() => {
    if (groupImages.length === 0) return hiddenExistingBoxIndices;
    return hiddenIndicesForImage(
      hiddenExistingBoxIndices,
      existingBoxImageIds,
      imageIdToFetch ?? "",
      effectiveImageId,
    );
  }, [
    groupImages.length,
    hiddenExistingBoxIndices,
    existingBoxImageIds,
    imageIdToFetch,
    effectiveImageId,
  ]);

  const hiddenUserDrawnForImage = useMemo(() => {
    if (groupImages.length === 0) return hiddenUserDrawnBoxIndices;
    return hiddenIndicesForImage(
      hiddenUserDrawnBoxIndices,
      userDrawnBoxes.map((box) => box.imageId ?? null),
      imageIdToFetch ?? "",
      effectiveImageId,
    );
  }, [
    groupImages.length,
    hiddenUserDrawnBoxIndices,
    userDrawnBoxes,
    imageIdToFetch,
    effectiveImageId,
  ]);

  const getBoxViewLabel = useCallback(
    (boxKey: string): string | undefined => {
      if (groupImages.length === 0) return undefined;
      const primary = imageIdToFetch ?? "";
      let imageId: string;
      if (boxKey.startsWith("auto-")) {
        const index = parseInt(boxKey.slice(5), 10);
        imageId = existingBoxImageIds[index] ?? primary;
      } else {
        const box = userDrawnBoxes.find((b) => `user-${b.id}` === boxKey);
        if (!box) return undefined;
        imageId = boxImageId(box, primary);
      }
      const view = groupImages.find((image) => image.imageId === imageId);
      return view ? `View ${view.viewNumber}` : undefined;
    },
    [groupImages, existingBoxImageIds, userDrawnBoxes, imageIdToFetch],
  );

  const boxCountsByImage = useMemo(() => {
    const counts = new Map<string, number>();
    if (groupImages.length === 0) return counts;
    const primary = imageIdToFetch ?? "";
    const bump = (id: string) => counts.set(id, (counts.get(id) ?? 0) + 1);
    existingBoxImageIds.forEach((id, i) => {
      if (!deletedBoxIndices.has(i)) bump(id ?? primary);
    });
    // "draw-user" is an unblurred person, not an empty area.
    userDrawnBoxes
      .filter((box) => box.type !== "draw-user")
      .forEach((box) => bump(boxImageId(box, primary)));
    return counts;
  }, [
    groupImages.length,
    existingBoxImageIds,
    deletedBoxIndices,
    userDrawnBoxes,
    imageIdToFetch,
  ]);

  useEffect(() => {
    setActiveImageId(null);
  }, [activeTask?.id]);

  // Initialize coverage state from admin result
  const hasInitializedAdminResult = useRef(false);
  useEffect(() => {
    if (!isAdmin || !adminResult || hasInitializedAdminResult.current) return;
    hasInitializedAdminResult.current = true;

    // Box categories
    if (Array.isArray(adminResult.box_categories)) {
      const cats = new Map<string, BoxCategory>();
      adminResult.box_categories.forEach((cat: string, i: number) => {
        if (cat === "oos_confirmed" || cat === "empty_area") {
          cats.set(`auto-${i}`, cat);
        }
      });
      setBoxCategories(cats);
    }

    // Product selections — try product_selections, then out_of_stock_confirmations,
    // then top-level confirmation_product_id(s) (old single-task OOS format).
    // Fall through each branch if it yields no results.
    let productSels: Map<string, any> | null = null;

    // 1. New coverage format: product_selections array
    if (!productSels && Array.isArray(adminResult.product_selections)) {
      const sels = new Map<string, any>();
      adminResult.product_selections.forEach((sel: any, i: number) => {
        if (sel && sel.product_ids?.length > 0) {
          sels.set(`auto-${i}`, sel);
        }
      });
      if (sels.size > 0) productSels = sels;
    }

    // 2. Coverage/OOS format: out_of_stock_confirmations array
    //    Each entry may have confirmation_product_ids (array) or confirmation_product_id (single)
    if (!productSels && Array.isArray(adminResult.out_of_stock_confirmations)) {
      const sels = new Map<string, any>();
      for (const conf of adminResult.out_of_stock_confirmations) {
        const ids =
          conf.confirmation_product_ids?.length > 0
            ? conf.confirmation_product_ids
            : conf.confirmation_product_id
              ? [conf.confirmation_product_id]
              : [];
        if (ids.length > 0) {
          const idx = conf.query_empty_area_index ?? 0;
          sels.set(`auto-${idx}`, {
            empty_area_index: idx,
            product_ids: ids,
            planogram_id: conf.override_planogram_id ?? "",
            bay_index: 0,
          });
        }
      }
      if (sels.size > 0) productSels = sels;
    }

    // 3. Old single-task OOS format: top-level confirmation_product_id(s)
    if (!productSels) {
      const adminResultAny = adminResult as any;
      const ids: unknown[] =
        adminResultAny.confirmation_product_ids?.length > 0
          ? adminResultAny.confirmation_product_ids
          : adminResultAny.confirmation_product_id
            ? [adminResultAny.confirmation_product_id]
            : [];
      if (ids.length > 0) {
        const idx = adminResultAny.metadata?.grouped_oos_events_index ?? 0;
        const sels = new Map<string, any>();
        sels.set(`auto-${idx}`, {
          empty_area_index: idx,
          product_ids: ids,
          planogram_id: adminResultAny.override_planogram_id ?? "",
          bay_index: 0,
        });
        productSels = sels;
      }
    }
    if (productSels) {
      setProductSelections(productSels);

      // If no box_categories in result but we have product selections,
      // default those boxes to "oos_confirmed" so they render correctly
      if (!Array.isArray(adminResult.box_categories)) {
        const cats = new Map<string, BoxCategory>();
        for (const key of productSels.keys()) {
          cats.set(key, "oos_confirmed");
        }
        setBoxCategories(cats);
      }
    }

    // Box planogram slot (aisle assignment)
    if (Array.isArray(adminResult.empty_area_to_aisle)) {
      const slots = new Map<string, number>();
      adminResult.empty_area_to_aisle.forEach(
        (aisleIdx: number | null, i: number) => {
          if (aisleIdx != null && aisleIdx >= 0) {
            slots.set(`auto-${i}`, aisleIdx);
          }
        },
      );
      setBoxPlanogramSlot(slots);
    }

    // Planogram overrides
    if (adminResult.override_planogram_ids) {
      const overrides = new Map<number, string>();
      for (const [slot, id] of Object.entries(
        adminResult.override_planogram_ids,
      )) {
        overrides.set(Number(slot), id as string);
      }
      setOverridePlanogramIds(overrides);
    }
  }, [
    isAdmin,
    adminResult,
    setBoxCategories,
    setProductSelections,
    setBoxPlanogramSlot,
    setOverridePlanogramIds,
  ]);

  // Scope the planogram search to the task's store (job.store), never
  // planogram.store_ids — that's the template's chain-wide deployment list.
  const retailerId = activePlanogramData?.retailer?.id ?? null;
  const storeId = job?.store?.atlas_id ?? null;

  // Auto-assign existing boxes to slots on mount using empty_area_to_aisle
  const hasAssignedAutoBoxes = useRef(false);
  const emptyAreaToAisle = useMemo(() => {
    return (
      ((parcel?.query as any)?.empty_area_to_aisle as (number | null)[]) ?? []
    );
  }, [parcel?.query]);

  useEffect(() => {
    if (hasAssignedAutoBoxes.current) return;
    if (emptyAreasPoints.length === 0) return;
    if (planogramSlots.length <= 1) return; // single planogram, all slot 0

    hasAssignedAutoBoxes.current = true;
    emptyAreaToAisle.forEach((aisleIdx, boxIndex) => {
      if (aisleIdx != null && aisleIdx >= 0) {
        assignBoxToSlot(`auto-${boxIndex}`, aisleIdx);
      }
    });
  }, [
    emptyAreasPoints.length,
    emptyAreaToAisle,
    planogramSlots.length,
    assignBoxToSlot,
  ]);

  // Handler for bulk planogram modal submit
  const handlePlanogramModalSubmit = useCallback(
    (slots: PlanogramSlotEdit[]) => {
      const newOverrides = new Map(overridePlanogramIds);
      const newExtras: string[] = [];

      for (const slot of slots) {
        if (slot.aisleIndex >= 0 && slot.aisleIndex < basePlanogramIds.length) {
          // Existing slot — check if planogram changed
          const baseId = basePlanogramIds[slot.aisleIndex];
          const currentOverride = overridePlanogramIds.get(slot.aisleIndex);
          const currentId = currentOverride ?? baseId;
          if (slot.planogramId && slot.planogramId !== currentId) {
            newOverrides.set(slot.aisleIndex, slot.planogramId);
            clearSelectionsForSlot(slot.aisleIndex);
          }
        } else if (slot.aisleIndex === -1 && slot.planogramId) {
          // Newly added slot
          newExtras.push(slot.planogramId);
        }
      }

      setOverridePlanogramIds(newOverrides);
      setExtraPlanogramIds((prev) => [...prev, ...newExtras]);
      setModalPreselectedSlot(null);
    },
    [basePlanogramIds, overridePlanogramIds, clearSelectionsForSlot],
  );

  const [rightPanelView, setRightPanelView] = useState<
    "planogram" | "detections"
  >("detections");
  const [viewBeforeSelection, setViewBeforeSelection] = useState<
    "planogram" | "detections" | null
  >(null);

  // --- Product-detection overlay (read-only aid) ---
  // Product IDs present in the currently shown planogram (active tab) — used to
  // decide which detections resolve to a highlightable product.
  const activePlanogramProductIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of activePlanogramData?.products ?? []) {
      if (p.product_id) set.add(p.product_id);
    }
    return set;
  }, [activePlanogramData]);

  const productDetections = useMemo(
    () => resolveProductDetections(parcel, activePlanogramProductIds),
    [parcel, activePlanogramProductIds],
  );

  // Hover-tooltip labels for detection boxes (indexed like productDetections)
  const detectionLabels = useMemo(() => {
    const map = new Map<number, { name: string; imageUrl?: string }>();
    productDetections.productIdByIndex.forEach((pid, i) => {
      if (!pid) return;
      map.set(i, {
        name: productNameMap.get(pid) ?? "Product",
        imageUrl: productImageUrls.get(pid),
      });
    });
    return map;
  }, [productDetections, productNameMap, productImageUrls]);

  // Product highlighted on the planogram while hovering a detection box
  const [hoveredDetectionProductId, setHoveredDetectionProductId] = useState<
    string | null
  >(null);
  // Product the planogram should pan/zoom to (set on detection click)
  const [focusProductId, setFocusProductId] = useState<string | null>(null);

  const handleDetectionHover = useCallback(
    (i: number | null) => {
      setHoveredDetectionProductId(
        i == null ? null : (productDetections.productIdByIndex[i] ?? null),
      );
    },
    [productDetections],
  );

  const handleDetectionClick = useCallback(
    (i: number) => {
      if (!productDetections.highlightableIndices.has(i)) return;
      const pid = productDetections.productIdByIndex[i];
      if (!pid) return;
      setRightPanelView("planogram");
      // Reset then set on the next frame so re-clicking the SAME product still
      // re-fires PlanogramViewer's initialProductId pan/zoom effect.
      setFocusProductId(null);
      requestAnimationFrame(() => setFocusProductId(pid));
    },
    [productDetections],
  );

  // Check if there are unassigned auto-detections that need categorizing
  const hasUnassigned = getUnassignedCount(emptyAreasPoints.length) > 0;

  // Force detections view when unassigned boxes first appear (e.g. on load
  // or after new auto-detections arrive). Only triggers on hasUnassigned
  // transitions so users can still manually switch to the planogram view.
  useEffect(() => {
    if (hasUnassigned) {
      setRightPanelView("detections");
    }
  }, [hasUnassigned]);

  // Training walkthrough state
  const [trainingActive, setTrainingActive] = useState(
    () => !isAdmin && !localStorage.getItem("coverage-training-dismissed"),
  );

  // Global Escape key handler — close the topmost overlay
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Priority order: select-item prompt > tutorial > planogram modal
      if (pendingSelectionBoxKey) {
        dismissProductSelection();
      } else if (trainingActive) {
        setTrainingActive(false);
      } else if (planogramSearchOpen) {
        setPlanogramSearchOpen(false);
        setModalPreselectedSlot(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [
    pendingSelectionBoxKey,
    dismissProductSelection,
    trainingActive,
    planogramSearchOpen,
  ]);

  const handleTrainingStepChange = useCallback((stepIndex: number) => {
    // Close modal unless we're on the modal step
    if (stepIndex !== 10) {
      setPlanogramSearchOpen(false);
    }

    switch (stepIndex) {
      case 6: // Planogram Viewer
      case 9: // Multiple Planograms
        setRightPanelView("planogram");
        break;
      case 7: // Categorize Detections
      case 8: // Detection Actions
        setRightPanelView("detections");
        break;
      case 10: // Planogram Search Modal
        setRightPanelView("planogram");
        setPlanogramSearchOpen(true);
        break;
    }
  }, []);

  // UI state — draw-empty maps to "draw" mode in BoxDrawImage
  const [drawMode, setDrawMode] = useState<
    "cursor" | "draw" | "draw-empty" | "draw-user"
  >("cursor");
  const [brightness, setBrightness] = useState(100);
  const [boxesVisible, setBoxesVisible] = useState(true);
  const [resetViewKey, setResetViewKey] = useState(0);
  const zoomRef = useRef<ReactZoomPanPinchRef>(null);

  // Build box image overlays map for BoxDrawImage (shows first product image on box)
  const boxImageOverlays = useMemo(() => {
    const map = new Map<number, string>();
    const existingCount = emptyAreasPoints.length;
    // Existing boxes
    for (let i = 0; i < existingCount; i++) {
      const selection = productSelections.get(getBoxKey(i, false));
      if (selection && selection.product_ids.length > 0) {
        // Show first product image
        const imageUrl = productImageUrls.get(selection.product_ids[0]);
        if (imageUrl) map.set(i, imageUrl);
      }
    }
    // User-drawn boxes
    userDrawnBoxes.forEach((_, i) => {
      const selection = productSelections.get(getBoxKey(i, true));
      if (selection && selection.product_ids.length > 0) {
        // Show first product image
        const imageUrl = productImageUrls.get(selection.product_ids[0]);
        if (imageUrl) map.set(existingCount + i, imageUrl);
      }
    });
    return map;
  }, [
    emptyAreasPoints.length,
    userDrawnBoxes,
    productSelections,
    getBoxKey,
    productImageUrls,
  ]);

  // Build box product labels for hover tooltips on BoxDrawImage
  const boxProductLabels = useMemo(() => {
    const map = new Map<number, { name: string; imageUrl?: string }>();
    const existingCount = emptyAreasPoints.length;
    for (let i = 0; i < existingCount; i++) {
      const selection = productSelections.get(getBoxKey(i, false));
      if (selection && selection.product_ids.length > 0) {
        const count = selection.product_ids.length;
        const firstProductName =
          productNameMap.get(selection.product_ids[0]) ?? "Selected product";
        const name =
          count > 1
            ? `${firstProductName} (+${count - 1} more)`
            : firstProductName;
        map.set(i, {
          name,
          imageUrl: productImageUrls.get(selection.product_ids[0]),
        });
      }
    }
    userDrawnBoxes.forEach((_, i) => {
      const selection = productSelections.get(getBoxKey(i, true));
      if (selection && selection.product_ids.length > 0) {
        const count = selection.product_ids.length;
        const firstProductName =
          productNameMap.get(selection.product_ids[0]) ?? "Selected product";
        const name =
          count > 1
            ? `${firstProductName} (+${count - 1} more)`
            : firstProductName;
        map.set(existingCount + i, {
          name,
          imageUrl: productImageUrls.get(selection.product_ids[0]),
        });
      }
    });
    return map;
  }, [
    emptyAreasPoints.length,
    userDrawnBoxes,
    productSelections,
    getBoxKey,
    productImageUrls,
    productNameMap,
  ]);

  // Build set of box indices that have confirmed product selections (at least one product)
  const confirmedBoxIndices = useMemo(() => {
    const set = new Set<number>();
    const existingCount = emptyAreasPoints.length;
    for (let i = 0; i < existingCount; i++) {
      const selection = productSelections.get(getBoxKey(i, false));
      if (selection && selection.product_ids.length > 0) set.add(i);
    }
    userDrawnBoxes.forEach((_, i) => {
      const selection = productSelections.get(getBoxKey(i, true));
      if (selection && selection.product_ids.length > 0)
        set.add(existingCount + i);
    });
    return set;
  }, [emptyAreasPoints.length, userDrawnBoxes, productSelections, getBoxKey]);

  // Persistent display number assignments for user-drawn boxes (keyed by box.id)
  // Numbers are assigned once when a box is created and never change, even after other boxes are deleted
  const userBoxNumbersRef = useRef<Map<string, number>>(new Map());
  const nextUserBoxNumberRef = useRef(0);

  // Build stable number labels for boxes on the image
  const boxNumberLabels = useMemo(() => {
    const map = new Map<number, number>();
    const existingCount = emptyAreasPoints.length;
    // Existing boxes — all get sequential numbers (even deleted ones, which are hidden)
    for (let i = 0; i < existingCount; i++) {
      map.set(i, i + 1);
    }
    // User-drawn boxes (OOS, empty area, person) — unified persistent numbering
    const baseNumber = existingCount;
    userDrawnBoxes.forEach((box, i) => {
      if (!userBoxNumbersRef.current.has(box.id)) {
        nextUserBoxNumberRef.current++;
        userBoxNumbersRef.current.set(box.id, nextUserBoxNumberRef.current);
      }
      map.set(
        existingCount + i,
        baseNumber + userBoxNumbersRef.current.get(box.id)!,
      );
    });
    return map;
  }, [emptyAreasPoints.length, userDrawnBoxes]);

  // Map our draw modes to BoxDrawImage's supported modes
  const boxDrawMode =
    drawMode === "draw-empty"
      ? "draw"
      : drawMode === "cursor"
        ? "pan"
        : drawMode;

  // Listen for planogram product clicks via the Zustand store (shared
  // module-level singleton — not scoped to this task or component).
  const activePlanoProduct = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo,
  );
  const resetPlanoProductStore = useActivePlanoProductInfoStore((s) => s.reset);

  const armProductSelection = useCallback(
    (boxKey: string) => {
      resetPlanoProductStore();
      requestProductSelection(boxKey);
    },
    [resetPlanoProductStore, requestProductSelection],
  );
  const activeTaskId = activeTask?.id;

  useEffect(() => {
    resetPlanoProductStore();
  }, [activeTaskId, resetPlanoProductStore]);

  // When a new box is drawn, auto-categorize, assign to slot, and request product selection for OOS
  const handleUserDrawnBoxesChangeWithSelection = useCallback(
    (incoming: typeof userDrawnBoxes) => {
      const boxes = incoming.map((box) =>
        box.imageId ? box : { ...box, imageId: effectiveImageId },
      );
      handleUserDrawnBoxesChange(boxes);
      // If a new "draw" box was added, categorize it
      const prevDrawCount = userDrawnBoxes.filter(
        (b) => b.type === "draw",
      ).length;
      const newDrawCount = boxes.filter((b) => b.type === "draw").length;
      if (newDrawCount > prevDrawCount) {
        const newBox = boxes[boxes.length - 1];
        // Compute key directly from the new box's id (getBoxKey reads stale state)
        const boxKey = `user-${newBox.id}`;

        // Auto-assign to planogram slot
        if (planogramSlots.length > 1) {
          let slotIndex: number;
          // Aisle detections are in the primary's coordinate space, so they
          // can't place a box drawn on a similar view.
          if (aisleDetections.length > 0 && isPrimaryImageActive) {
            slotIndex = getAisleIndexForBox(newBox.points, aisleDetections);
          } else {
            slotIndex = activePlanogramTab;
          }
          assignBoxToSlot(boxKey, slotIndex);
          // Auto-switch to the correct tab
          setActivePlanogramTab(slotIndex);
        }

        if (drawMode === "draw-empty") {
          moveBoxToCategory(boxKey, "empty_area");
          // No product selection needed for empty areas
        } else if (drawMode === "draw") {
          moveBoxToCategory(boxKey, "oos_confirmed");
          // Force planogram view and block UI until product is selected
          setViewBeforeSelection(rightPanelView);
          setRightPanelView("planogram");
          armProductSelection(boxKey);
        }
      }
    },
    [
      handleUserDrawnBoxesChange,
      userDrawnBoxes,
      armProductSelection,
      drawMode,
      moveBoxToCategory,
      rightPanelView,
      planogramSlots.length,
      aisleDetections,
      assignBoxToSlot,
      activePlanogramTab,
      effectiveImageId,
      isPrimaryImageActive,
    ],
  );

  const handleMoveBoxToCategory = useCallback(
    (boxKey: string, category: BoxCategory) => {
      moveBoxToCategory(boxKey, category);
      if (category === "oos_confirmed" && !productSelections.has(boxKey)) {
        setViewBeforeSelection(rightPanelView);
        setRightPanelView("planogram");
        armProductSelection(boxKey);
      }
    },
    [moveBoxToCategory, productSelections, armProductSelection, rightPanelView],
  );

  // When a product is clicked in the planogram, toggle it in the selection
  const handleProductToggled = useCallback(
    (
      boxKey: string,
      productId: string,
      planogramId: string,
      bayIndex: number,
      emptyAreaIndex: number,
    ) => {
      toggleProductInSelection(
        boxKey,
        productId,
        planogramId,
        bayIndex,
        emptyAreaIndex,
      );
      // Ensure the box is associated with the planogram it was selected from
      if (planogramSlots.length > 1) {
        assignBoxToSlot(boxKey, activePlanogramTab);
      }
      // Don't auto-dismiss - user must click "Done" to confirm multi-selection
    },
    [
      toggleProductInSelection,
      planogramSlots.length,
      assignBoxToSlot,
      activePlanogramTab,
    ],
  );

  // When user confirms their multi-product selection
  const handleConfirmSelection = useCallback(() => {
    confirmProductSelection();
    // Restore previous view if we forced planogram
    if (viewBeforeSelection !== null) {
      setRightPanelView(viewBeforeSelection);
      setViewBeforeSelection(null);
    }
  }, [confirmProductSelection, viewBeforeSelection]);

  // Handle clicking an existing box to prompt product selection
  const handleBoxSelect = useCallback(
    (index: number, isUserDrawn: boolean) => {
      if (isUserDrawn) {
        setSelectedUserDrawnBoxIndex(index);
        setSelectedExistingBoxIndex(null);
      } else {
        setSelectedExistingBoxIndex(index);
        setSelectedUserDrawnBoxIndex(null);
      }
      if (groupImages.length > 0) {
        const target = isUserDrawn
          ? userDrawnBoxes[index]?.imageId
          : existingBoxImageIds[index];
        setActiveImageId(target ?? imageIdToFetch ?? null);
      }
      const boxKey = getBoxKey(index, isUserDrawn);

      // Auto-switch to the correct planogram tab for this box
      if (planogramSlots.length > 1) {
        const slot = getBoxSlot(boxKey);
        if (slot !== activePlanogramTab) {
          setActivePlanogramTab(slot);
        }
      }

      // Trigger product selection flow for OOS Confirmed boxes
      // Allow re-entering selection mode even if products are already selected (to add more)
      // Skip in admin mode — just select the box without entering selection flow
      if (!isAdmin && getBoxCategory(boxKey) === "oos_confirmed") {
        setViewBeforeSelection(rightPanelView);
        setRightPanelView("planogram");
        armProductSelection(boxKey);
      }
    },
    [
      getBoxKey,
      getBoxCategory,
      armProductSelection,
      setSelectedExistingBoxIndex,
      setSelectedUserDrawnBoxIndex,
      rightPanelView,
      planogramSlots.length,
      getBoxSlot,
      activePlanogramTab,
      groupImages.length,
      userDrawnBoxes,
      existingBoxImageIds,
      imageIdToFetch,
      isAdmin,
    ],
  );

  // Get the currently selected product IDs for the pending selection box
  const currentSelectedProductIds = useMemo(() => {
    if (isAdmin) {
      // In admin mode, show all product IDs from all OOS detections
      const allIds = new Set<string>();
      for (const [, sel] of productSelections) {
        for (const id of sel.product_ids ?? []) {
          allIds.add(id);
        }
      }
      return Array.from(allIds);
    }
    if (!pendingSelectionBoxKey) return [];
    return getSelectedProductIds(pendingSelectionBoxKey);
  }, [
    isAdmin,
    productSelections,
    pendingSelectionBoxKey,
    getSelectedProductIds,
  ]);

  const currentSelectedCount = pendingSelectionBoxKey
    ? getSelectedProductCount(pendingSelectionBoxKey)
    : 0;

  // Track the last product_id we consumed to avoid re-triggering on the same click
  const lastConsumedProductRef = useRef<string>("");

  useEffect(() => {
    if (
      pendingSelectionBoxKey &&
      activePlanoProduct.product_id &&
      activePlanoProduct.product_id !== "" &&
      activePlanoProduct.product_id !== lastConsumedProductRef.current
    ) {
      lastConsumedProductRef.current = activePlanoProduct.product_id;
      // For auto boxes key is "auto-{index}", for user boxes key is "user-{uuid}"
      // Extract the index for auto boxes; for user boxes, find the array index
      let emptyAreaIndex = 0;
      if (pendingSelectionBoxKey.startsWith("auto-")) {
        emptyAreaIndex = parseInt(pendingSelectionBoxKey.slice(5), 10);
      } else if (pendingSelectionBoxKey.startsWith("user-")) {
        const boxId = pendingSelectionBoxKey.slice(5);
        const idx = userDrawnBoxes.findIndex((b) => b.id === boxId);
        if (idx === -1) {
          // Box no longer exists — don't misassign to index 0
          resetPlanoProductStore();
          lastConsumedProductRef.current = "";
          return;
        }
        emptyAreaIndex = emptyAreasPoints.length + idx;
      }
      // Toggle the product in the selection (add if not present, remove if present)
      handleProductToggled(
        pendingSelectionBoxKey,
        activePlanoProduct.product_id,
        activePlanogramId ?? "unknown",
        0,
        emptyAreaIndex,
      );
      // Reset the store so stale values don't re-trigger
      resetPlanoProductStore();
      // Clear the ref so the same product can be toggled again
      // (deselect within session, or select for a different box)
      lastConsumedProductRef.current = "";
    }
  }, [
    activePlanoProduct.product_id,
    activePlanoProduct.planogram_product_id,
    pendingSelectionBoxKey,
    handleProductToggled,
    activePlanogramId,
    resetPlanoProductStore,
    userDrawnBoxes,
    emptyAreasPoints.length,
  ]);

  // Submit handler
  const { mutateAsync: completeJobTask, isPending: isSubmitting } =
    useCompleteJobTask(jobId ?? "", activeTask?.id ?? "");

  const handleSubmit = async () => {
    if (!jobId || !activeTask?.id) return;
    if (!canSubmit(emptyAreasPoints.length)) return;

    const primaryImageId = imageIdToFetch ?? "";
    const originalAreas = nextPendingTaskData?.data?.empty_areas ?? [];
    const confirmedExisting = originalAreas
      .map((area: any, i: number) => ({ area, i }))
      .filter(({ i }: { i: number }) => !deletedBoxIndices.has(i))
      .map(({ area, i }: { area: any; i: number }) =>
        withImageId(area, existingBoxImageIds[i] ?? primaryImageId),
      );
    const drawnAreas = userDrawnBoxes
      .filter((box) => box.type === "draw")
      .map((box) =>
        withImageId(
          {
            id: crypto.randomUUID(),
            points: box.points,
            confidence: 1.0,
            class_index: 0,
          },
          boxImageId(box, primaryImageId),
        ),
      );
    const allEmptyAreas = [...confirmedExisting, ...drawnAreas];
    const emptyAreasSource: ("auto" | "human")[] = allEmptyAreas.map(
      (_, index) => (index < confirmedExisting.length ? "auto" : "human"),
    );

    const unblurredPersons = userDrawnBoxes
      .filter((box) => box.type === "draw-user")
      .map((box) =>
        withImageId(
          { points: box.points, confidence: 1.0, class_index: 0 },
          boxImageId(box, primaryImageId),
        ),
      );

    // Resolve box keys aligned with allEmptyAreas
    const originalIndices = Array.from(
      { length: originalAreas.length },
      (_: any, i: number) => i,
    ).filter((i) => !deletedBoxIndices.has(i));

    const resolveBoxKey = (index: number): string => {
      if (index < confirmedExisting.length) {
        return getBoxKey(originalIndices[index], false);
      }
      const drawIndex = index - confirmedExisting.length;
      const drawBoxActualIndex = userDrawnBoxes
        .map((b, i) => ({ b, i }))
        .filter(({ b }) => b.type === "draw")[drawIndex]?.i;
      return getBoxKey(drawBoxActualIndex ?? 0, true);
    };

    // Build product_selections array aligned with allEmptyAreas
    const productSelectionsArray = allEmptyAreas.map((_, index) => {
      const boxKey = resolveBoxKey(index);
      const selection = productSelections.get(boxKey);
      return {
        empty_area_index: index,
        product_ids: selection?.product_ids ?? [],
        planogram_id: selection?.planogram_id ?? "",
        bay_index: selection?.bay_index ?? 0,
      };
    });

    // Build box_categories array aligned with allEmptyAreas
    const boxCategories = allEmptyAreas.map((_, index) => {
      const boxKey = resolveBoxKey(index);
      const cat = getBoxCategory(boxKey);
      // Default unassigned to oos_confirmed (they have product selections to submit)
      return cat === "empty_area"
        ? ("empty_area" as const)
        : ("oos_confirmed" as const);
    });

    // Build out_of_stock_confirmations for backend event publishing
    const outOfStockConfirmations = allEmptyAreas
      .map((_, index) => {
        const boxKey = resolveBoxKey(index);
        const cat = getBoxCategory(boxKey);
        if (cat !== "oos_confirmed") return null;

        const selection = productSelections.get(boxKey);
        const slot = getBoxSlot(boxKey);
        const overridePlanogramId = overridePlanogramIds.get(slot) ?? null;

        return {
          query_empty_area_index: index,
          // Use first product_id for backward compatibility, plus full array
          confirmation_product_id: selection?.product_ids?.[0] ?? null,
          confirmation_product_ids: selection?.product_ids ?? [],
          override_planogram_id: overridePlanogramId,
          identified_product_id: null,
          product_index: null,
          reference_indices: null,
        };
      })
      .filter(Boolean);

    // Build empty_area_to_aisle from boxPlanogramSlot
    const emptyAreaToAisleResult = allEmptyAreas.map((_, index) => {
      const boxKey = resolveBoxKey(index);
      return getBoxSlot(boxKey);
    });

    // Build override_planogram_ids map (includes both overridden base slots and extra planograms)
    const overrideMap: Record<number, string> = {};
    overridePlanogramIds.forEach((id, slot) => {
      overrideMap[slot] = id;
    });
    extraPlanogramIds.forEach((id, i) => {
      overrideMap[basePlanogramIds.length + i] = id;
    });

    const result = {
      deleted_empty_areas: Array.from(deletedBoxIndices),
      empty_areas: allEmptyAreas,
      empty_areas_source: emptyAreasSource,
      empty_area_to_aisle: emptyAreaToAisleResult,
      unblurred_persons: unblurredPersons,
      product_selections: productSelectionsArray,
      box_categories: boxCategories,
      out_of_stock_confirmations: outOfStockConfirmations,
      ...(Object.keys(overrideMap).length > 0
        ? { override_planogram_ids: overrideMap }
        : {}),
      metadata: { image_quality_ok: true },
      testing: job?.testing ?? false,
    };

    // Dev mock: log the result instead of writing to the backend (staging).
    // Inert in prod — guarded by import.meta.env.DEV && an active fixture.
    if (import.meta.env.DEV && devFixtures.active) {
      console.log("[DevFixture] Coverage submit result:", result);
      return;
    }

    try {
      await completeJobTask({ result });

      resetCoverageState();
      userBoxNumbersRef.current = new Map();
      nextUserBoxNumberRef.current = 0;
      setOverridePlanogramIds(new Map());
      setExtraPlanogramIds([]);
      setActivePlanogramTab(0);
      hasAssignedAutoBoxes.current = false;
      advanceToNextTask();
    } catch (error) {
      console.error("Failed to submit coverage task:", error);
    }
  };

  // Skip handler
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipReason, setSkipReason] = useState<string | null>(null);
  const { mutateAsync: skipJobTask, isPending: isSkipping } = useSkipJobTask(
    jobId ?? "",
    activeTask?.id ?? "",
  );

  const handleSkip = async () => {
    if (!jobId || !activeTask?.id || !skipReason) return;

    // Dev mock: log instead of writing to the backend (staging).
    if (import.meta.env.DEV && devFixtures.active) {
      console.log("[DevFixture] Coverage skip:", {
        skip_reasons: [skipReason],
      });
      setSkipModalOpen(false);
      setSkipReason(null);
      return;
    }

    try {
      await skipJobTask({
        result: {
          skip_reasons: [skipReason],
          skip_notes: null,
        },
      });
      setSkipModalOpen(false);
      setSkipReason(null);
      resetCoverageState();
      userBoxNumbersRef.current = new Map();
      nextUserBoxNumberRef.current = 0;
      setOverridePlanogramIds(new Map());
      setExtraPlanogramIds([]);
      setActivePlanogramTab(0);
      hasAssignedAutoBoxes.current = false;
      advanceToNextTask();
    } catch (error) {
      console.error("Failed to skip coverage task:", error);
    }
  };

  // Optimistically flip to the prefetched next task on submit/skip.
  // If the prefetch isn't fully ready, drop the cache so a LoadingOverlay
  // appears during refetch instead of re-rendering the task just submitted.
  const advanceToNextTask = () => {
    if (!jobId) return;
    let flipped = false;
    if (isPrefetchReady && prefetchNextTaskId) {
      const prefetched = queryClient.getQueryData<JobTaskDetailSchema>([
        "fetch-task",
        jobId,
        prefetchNextTaskId,
      ]);
      if (prefetched) {
        queryClient.setQueryData(["nextPendingTask", jobId], prefetched);
        flipped = true;
      }
    }
    queryClient.invalidateQueries({ queryKey: ["job-tasks", jobId] });
    if (flipped) {
      queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
    } else {
      queryClient.removeQueries({ queryKey: ["nextPendingTask", jobId] });
    }
  };

  const isLoading =
    mainImageLoading ||
    (!isPrimaryImageActive && siblingImagesLoading) ||
    (!isAdmin && taskLoading) ||
    (!isAdmin && parcelLoading) ||
    (isBenchmarkTask && benchmarkGateLoading);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingOverlay visible />
      </div>
    );
  }

  // Benchmark quiz tasks are served only to the treatment group; everyone
  // else gets the same empty state as an exhausted job.
  if (isBenchmarkTask && !benchmarkEnabled) {
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button onClick={() => navigate(jobsPath)}>Go to dashboard</Button>
        </div>
      </div>
    );
  }

  // A finished quiz lands here — taskError fires once there's no next pending
  // task to serve. Show the reviewer how they did instead of silently
  // navigating away (the taskError effect above already exempts benchmark
  // jobs from that navigation) or falling through to render the main editor
  // with no task — this branch always returns something once it's entered.
  if (!isAdmin && taskError && isBenchmarkJob) {
    if (benchmarkEnabled && quizMetrics?.status === JobStatus.COMPLETED) {
      return (
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <QuizMetricCard
            title="Quiz complete"
            status={quizMetrics.status as JobStatus}
            subtitle={`${quizMetrics.store_name} · ${new Date(
              quizMetrics.created_at,
            ).toLocaleString()}`}
            precision={quizMetrics.precision}
            correct={quizMetrics.correct}
            answered={quizMetrics.answered}
            avgSecondsPerTask={quizMetrics.avg_seconds_per_task}
            tasksPerHour={quizMetrics.tasks_per_hour}
            onBack={() => navigate(`${jobsPath}?tab=my-quizzes`)}
          />
        </div>
      );
    }
    if (benchmarkEnabled && quizMetricsLoading) {
      return (
        <div className="flex justify-center items-center h-40 relative">
          <LoadingOverlay visible={true} />
        </div>
      );
    }
    // Gate off, or the gate is on but metrics haven't resolved to COMPLETED
    // (quiz not actually finished yet, or the fetch failed) — generic empty
    // state rather than a card claiming results that aren't there.
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button onClick={() => navigate(jobsPath)}>Go to dashboard</Button>
        </div>
      </div>
    );
  }

  const submitDisabled = !canSubmit(emptyAreasPoints.length);

  return (
    <div
      className="relative"
      style={{
        marginTop: isAdmin ? 0 : -HEADER_HEIGHT,
        height: isAdmin ? `calc(100vh - ${HEADER_HEIGHT * 2}px)` : "100vh",
      }}
    >
      {/* Top bar — hidden in admin mode (AdminToolbar replaces it) */}
      {!isAdmin && (
        <div
          className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20 flex items-center justify-between px-4"
          style={{ height: HEADER_HEIGHT }}
        >
          <div className="flex items-center gap-2">
            <Text fw={600}>Out of Stock</Text>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => setTrainingActive(true)}
              title="Show training walkthrough"
            >
              <IconHelpCircle size={18} />
            </ActionIcon>
          </div>
          <div
            className="flex items-center gap-2"
            data-training="top-bar-actions"
          >
            <TaskCounter taskId={activeTask?.id} />
            <div className="w-px h-5 bg-gray-300" />
            {isPlayMode && (
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconPlayerStopFilled size={16} />}
                onClick={() => {
                  dispatch(setIsPlayMode(false));
                  navigate(`${jobsPath}?tab=coverage`);
                }}
              >
                Play Mode
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                dispatch(setIsPlayMode(false));
                navigate(jobsPath);
              }}
            >
              Exit
            </Button>
            <Button
              variant="outline"
              color="orange"
              size="sm"
              onClick={() => setSkipModalOpen(true)}
            >
              Skip
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={submitDisabled}
              title={
                submitDisabled
                  ? "Categorize all detections and select products before submitting"
                  : undefined
              }
            >
              Submit
            </Button>
          </div>
        </div>
      )}

      {/* Left sidebar */}
      <CoverageSidebar
        drawMode={drawMode}
        onDrawModeChange={setDrawMode}
        brightness={brightness}
        onBrightnessChange={setBrightness}
        boxesVisible={boxesVisible}
        onToggleBoxVisibility={() => setBoxesVisible((v) => !v)}
        onResetView={() => {
          setResetViewKey((k) => k + 1);
          setBrightness(100);
        }}
        onZoomIn={() => zoomRef.current?.zoomIn(0.5)}
        onZoomOut={() => zoomRef.current?.zoomOut(0.5)}
        drawingDisabled={hasUnassigned}
        readOnly={isAdmin}
      />

      {/* Main content area — 50/50 split */}
      <div
        className="absolute top-0 bottom-0 flex flex-col"
        style={{
          top: HEADER_HEIGHT,
          left: COVERAGE_SIDEBAR_WIDTH,
          right: 0,
        }}
      >
        {/* Floating notification for unassigned detections */}
        {hasUnassigned && (
          <div className="fixed bottom-4 right-4 z-50 px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg shadow-lg">
            <Text size="sm" fw={600} c="orange">
              Categorize all detections before drawing new boxes
            </Text>
          </div>
        )}
        <div className="flex flex-1 min-h-0">
          {/* Shelf image — 50% */}
          <div
            className="relative flex-1 min-w-0 flex flex-col"
            data-training="shelf-image"
          >
            {/* Dim overlay when waiting for product selection. pointer-events-none
                so product-detection hover/click still reach the shelf image —
                box drawing/editing is disabled below instead. */}
            {pendingSelectionBoxKey && (
              <div className="absolute inset-0 z-10 bg-black/10 pointer-events-none" />
            )}

            {/* Shelf image with box drawing */}
            {shelfImageUrl && (
              <div
                style={{ filter: `brightness(${brightness}%)` }}
                className="flex-1 min-h-0 w-full flex justify-center items-center"
              >
                <BoxDrawImage
                  key={`${resetViewKey}-${effectiveImageId}`}
                  src={shelfImageUrl}
                  alt="Coverage Task"
                  existingBoxes={boxesVisible ? emptyAreasPoints : []}
                  hiddenExistingBoxIndices={hiddenExistingForImage}
                  selectedExistingBoxIndex={selectedExistingBoxIndex}
                  onSelectedExistingBoxChange={setSelectedExistingBoxIndex}
                  userDrawnBoxes={boxesVisible ? userDrawnBoxes : []}
                  hiddenUserDrawnBoxIndices={hiddenUserDrawnForImage}
                  onUserDrawnBoxesChange={
                    handleUserDrawnBoxesChangeWithSelection
                  }
                  selectedUserDrawnBoxIndex={selectedUserDrawnBoxIndex}
                  onSelectedUserDrawnBoxChange={setSelectedUserDrawnBoxIndex}
                  defaultMode={isAdmin ? "pan" : boxDrawMode}
                  variant="empty"
                  enableZoom
                  enableDrawing={!isAdmin && !pendingSelectionBoxKey}
                  enableDeletion={!isAdmin && !pendingSelectionBoxKey}
                  showModeControls={false}
                  zoomRef={zoomRef}
                  boxImageOverlays={boxImageOverlays}
                  useNumbers
                  boxNumberLabels={boxNumberLabels}
                  confirmedBoxIndices={confirmedBoxIndices}
                  boxProductLabels={boxProductLabels}
                  detectionBoxes={
                    boxesVisible && isPrimaryImageActive
                      ? productDetections.boxes
                      : []
                  }
                  detectionBoxLabels={detectionLabels}
                  highlightableDetectionIndices={
                    productDetections.highlightableIndices
                  }
                  onDetectionHover={handleDetectionHover}
                  onDetectionClick={handleDetectionClick}
                />
              </div>
            )}

            <ImageFilmstrip
              images={groupImages}
              imageUrls={groupImageUrls}
              activeImageId={effectiveImageId}
              onSelect={setActiveImageId}
              boxCounts={boxCountsByImage}
            />
          </div>

          {/* Right panel — 50% */}
          <div className="flex-1 min-w-0" data-training="right-panel">
            <CoverageRightPanel
              planogramId={activePlanogramData?.id ?? ""}
              planogramLoading={activePlanogramLoading}
              selectedProductIds={currentSelectedProductIds}
              highlightedProductIds={
                hoveredDetectionProductId
                  ? [hoveredDetectionProductId]
                  : undefined
              }
              focusProductId={focusProductId}
              existingBoxes={emptyAreasPoints}
              userDrawnBoxes={userDrawnBoxes}
              hiddenExistingBoxIndices={hiddenExistingBoxIndices}
              hiddenUserDrawnBoxIndices={hiddenUserDrawnBoxIndices}
              deletedBoxIndices={deletedBoxIndices}
              selectedExistingBoxIndex={selectedExistingBoxIndex}
              selectedUserDrawnBoxIndex={selectedUserDrawnBoxIndex}
              productSelections={productSelections}
              getBoxKey={getBoxKey}
              getBoxCategory={getBoxCategory}
              moveBoxToCategory={handleMoveBoxToCategory}
              onBoxView={handleBoxView}
              onBoxDelete={handleBoxDelete}
              onBoxRestore={handleBoxRestore}
              onBoxSelect={handleBoxSelect}
              onRemoveSelection={removeProductSelection}
              productImageUrls={productImageUrls}
              productNames={productNameMap}
              planogramNames={planogramNameMap}
              boxNumberLabels={boxNumberLabels}
              view={rightPanelView}
              onViewChange={setRightPanelView}
              isSelectionBlocked={!!pendingSelectionBoxKey}
              onIncorrectPlanogram={() => setPlanogramSearchOpen(true)}
              onReplacePlanogram={(slotIndex) => {
                setModalPreselectedSlot(slotIndex);
                setActivePlanogramTab(slotIndex);
                setPlanogramSearchOpen(true);
              }}
              onAddPlanogram={() => {
                setModalPreselectedSlot(null);
                setPlanogramSearchOpen(true);
              }}
              planogramSlots={planogramSlotLabels}
              activePlanogramTab={activePlanogramTab}
              onPlanogramTabChange={setActivePlanogramTab}
              getBoxSlot={getBoxSlot}
              getBoxViewLabel={getBoxViewLabel}
              assignBoxToSlot={assignBoxToSlot}
              readOnly={isAdmin}
              onViewAllPlanograms={
                isAdmin ? () => setPlanogramSearchOpen(true) : undefined
              }
              selectedCount={currentSelectedCount}
              onConfirmSelection={handleConfirmSelection}
              onDismissSelection={dismissProductSelection}
            />
          </div>
        </div>
      </div>

      {/* Training walkthrough overlay — hidden in admin mode */}
      {!isAdmin && (
        <CoverageTrainingOverlay
          active={trainingActive}
          onClose={() => {
            setTrainingActive(false);
            // Reset to default onload UI state
            setPlanogramSearchOpen(false);
            setRightPanelView("detections");
          }}
          onStepChange={handleTrainingStepChange}
        />
      )}

      {/* Skip Task Modal — hidden in admin mode */}
      {!isAdmin && (
        <Modal
          opened={skipModalOpen}
          onClose={() => {
            setSkipModalOpen(false);
            setSkipReason(null);
          }}
          title={<Text fw={600}>Skip Task</Text>}
          centered
          zIndex={10000}
          withinPortal
          portalProps={{ target: "body" }}
          lockScroll={false}
          styles={{
            overlay: { position: "fixed", inset: 0 },
            inner: { position: "fixed", inset: 0 },
          }}
        >
          <div className="flex flex-col gap-4">
            <Text size="sm" c="dimmed">
              Select a reason for skipping this task:
            </Text>
            <Radio.Group value={skipReason ?? ""} onChange={setSkipReason}>
              <div className="flex flex-col gap-2">
                <Radio
                  value="unsupported_planogram"
                  label="Unsupported Planogram"
                />
                <Radio value="poor_image_quality" label="Poor Image Quality" />
              </div>
            </Radio.Group>
            <Group justify="flex-end" mt="sm">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => {
                  setSkipModalOpen(false);
                  setSkipReason(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleSkip}
                disabled={!skipReason}
                loading={isSkipping}
              >
                Confirm Skip
              </Button>
            </Group>
          </div>
        </Modal>
      )}

      {/* Idle tracking — viewer mode only */}
      {!isAdmin && (
        <IdleTimeProvider
          taskId={activeTask?.id || ""}
          timeout={isDev() ? 1_000_000_000 : undefined} // don't show warning modal in dev env
        >
          <IdleWarningModal jobId={jobId || ""} taskId={activeTask?.id || ""} />
          <ExitModal />
        </IdleTimeProvider>
      )}

      {/* Planogram Search Modal */}
      <PlanogramSearchModal
        opened={planogramSearchOpen}
        onClose={() => {
          setPlanogramSearchOpen(false);
          setModalPreselectedSlot(null);
        }}
        onSubmit={handlePlanogramModalSubmit}
        onError={setPlanogramSearchError}
        retailerId={retailerId}
        storeId={storeId}
        currentSlots={planogramSlotLabels}
        preselectedSlot={modalPreselectedSlot}
        readOnly={isAdmin}
        showAllToggle
      />

      {/* Planogram error toast — suppressed in admin mode (read-only, no action to take) */}
      {!isAdmin && planogramSearchError && !planogramSearchOpen && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 rounded-lg shadow-lg">
          <div>
            <Text size="sm" fw={600} c="red">
              Planogram error ({planogramSearchError.code})
            </Text>
            <Text size="xs" c="dimmed">
              {planogramSearchError.message}
            </Text>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="text-xs font-medium text-blue-600 hover:text-blue-800 underline"
              onClick={() => setPlanogramSearchOpen(true)}
            >
              View Details
            </button>
            <button
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={() => setPlanogramSearchError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
